import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';

const Terms = () => {
  const lastUpdated = 'May 23, 2026';

  return (
    <div className="min-h-screen bg-[#f9f7f3]" data-testid="terms-page">
      <Navbar />

      {/* Hero */}
      <div className="bg-gradient-to-br from-[#434345] to-[#2a2a2c] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <p className="text-xs tracking-widest uppercase text-[#BCA480] mb-3">Legal</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">Terms &amp; Conditions</h1>
          <p className="text-sm text-gray-300">Last Updated: {lastUpdated}</p>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-lg shadow-sm p-8 sm:p-12 space-y-8 text-[#434345] leading-relaxed">

          <section>
            <p>
              These Terms &amp; Conditions (&quot;Terms&quot;) govern your access to and use of the website,
              services, and communications provided by Bespoke Realty (&quot;Bespoke Realty,&quot; &quot;we,&quot;
              &quot;us,&quot; or &quot;our&quot;). By using our website at{' '}
              <a href="https://bespokerealty.net" className="text-[#BCA480] hover:underline">bespokerealty.net</a>,
              submitting a form, or receiving communications from us, you agree to these Terms. If you do
              not agree, please do not use the site.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3">1. Brokerage Services</h2>
            <p>
              Bespoke Realty is a licensed real estate brokerage operating in Mississippi (License #24018),
              Tennessee (License #266066), and Arkansas (Principal Broker License #PB000099531). All real
              estate services are governed by the laws of the state in which the property is located and by
              the written representation agreement signed between you and Bespoke Realty.
            </p>
            <p className="mt-3">
              Use of this website does not by itself create an agency, fiduciary, or brokerage relationship.
              Such relationships are formed only by a signed agreement.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3">2. Property Information &amp; MLS Listings</h2>
            <p>
              Listings displayed on our website are sourced from the MAAR (Memphis Area Association of
              Realtors) and CMMLS (Central Mississippi MLS) via the SimplyRETS IDX feed. While we strive to
              keep this data current, we do not guarantee accuracy, availability, or completeness. All
              square footage, taxes, schools, and HOA information should be independently verified by the
              buyer.
            </p>
            <p className="mt-3">
              Listings are provided for the personal, non-commercial use of consumers and may not be used
              for any purpose other than to identify properties consumers may be interested in purchasing.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3">3. AI Assistants (&quot;Nova&quot; and &quot;Auri&quot;)</h2>
            <p>
              Our website features two AI assistants: <strong>Nova</strong> (a public-facing concierge) and{' '}
              <strong>Auri</strong> (an internal transaction coordinator for licensed agents). These
              assistants are powered by third-party AI models and are intended for informational and
              workflow purposes only. Their responses do not constitute legal, financial, or fiduciary
              advice. All material decisions in your transaction should be made in consultation with your
              licensed Bespoke Realty agent.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3">4. SMS &amp; Email Communications</h2>
            <p>
              <strong>Program name:</strong> Bespoke Realty SMS Program.
            </p>
            <p className="mt-3">
              By opting in on our website (via the SMS consent checkbox on any of our lead-capture
              forms), by providing your phone number in person, or by signing a buyer/seller
              representation agreement, you consent to receive recurring text messages and
              transactional emails from Bespoke Realty related to your real estate inquiry and
              transactions, including:
            </p>
            <ul className="list-disc pl-6 space-y-1 text-base mt-3">
              <li>Tour confirmations and scheduling updates.</li>
              <li>Transaction milestone reminders (inspections, appraisals, closing dates, document requests).</li>
              <li>Follow-ups from your assigned licensed agent and our AI transaction coordinator (&quot;Auri&quot;).</li>
              <li>Responses to inquiries you submit through our website forms.</li>
            </ul>
            <p className="mt-3">
              <strong>Message frequency:</strong> Recurring messages, up to 10 messages per month
              depending on your active tours and transactions.
            </p>
            <p className="mt-2">
              <strong>Message and data rates may apply</strong> from your mobile carrier. Carriers are
              not liable for delayed or undelivered messages.
            </p>
            <p className="mt-2">
              <strong>Opt-out:</strong> Reply <code className="bg-gray-100 px-1.5 py-0.5 rounded">STOP</code>{' '}
              to any message to unsubscribe at any time. You will receive a one-time confirmation that
              you have been unsubscribed and will not receive further messages.
            </p>
            <p className="mt-2">
              <strong>Help:</strong> Reply <code className="bg-gray-100 px-1.5 py-0.5 rounded">HELP</code>{' '}
              for assistance, or contact us at{' '}
              <a href="tel:9013904414" className="text-[#BCA480] hover:underline">901-390-4414</a> or{' '}
              <a href="mailto:office@bespokerealty.net" className="text-[#BCA480] hover:underline">office@bespokerealty.net</a>.
            </p>
            <p className="mt-3">
              <strong>Consent to receive SMS messages is not a condition of purchase</strong> or of
              using our real estate services.
            </p>
            <p className="mt-3">
              <strong>Privacy of mobile information:</strong> No mobile information will be shared with
              third parties or affiliates for marketing or promotional purposes. Information sharing
              related to SMS originator opt-in data and consent will not be shared with any third
              parties.
            </p>
            <p className="mt-3">
              See our{' '}
              <Link to="/privacy" className="text-[#BCA480] hover:underline" data-testid="link-to-privacy">Privacy Policy</Link>{' '}
              (including Section 4) for full details on our SMS program and how we handle your data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3">5. Equal Housing Opportunity</h2>
            <p>
              Bespoke Realty is committed to the principles of the Fair Housing Act and Equal Opportunity
              Act. We do not discriminate against any person because of race, color, religion, sex
              (including gender identity and sexual orientation), familial status, national origin,
              disability, or any other class protected by federal, state, or local law.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3">6. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul className="list-disc pl-6 space-y-1 text-base mt-2">
              <li>Use the site for unlawful purposes or in violation of these Terms.</li>
              <li>Scrape, copy, or republish MLS listings or other content without written permission.</li>
              <li>Submit false, misleading, or fraudulent information through our forms.</li>
              <li>Attempt to compromise the security or integrity of the site or our systems.</li>
              <li>Use automated tools to access the site without our written consent.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3">7. Intellectual Property</h2>
            <p>
              All website content — including the Bespoke Realty name, logo, branding, photography, written
              materials, and design — is the property of Bespoke Realty or its licensors and is protected by
              U.S. copyright and trademark laws. You may not reproduce, redistribute, or create derivative
              works without our prior written permission.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3">8. Third-Party Links</h2>
            <p>
              Our website may link to third-party sites (lenders, inspectors, government resources, etc.).
              We are not responsible for the content, privacy practices, or services of those third-party
              sites. Access them at your own risk.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3">9. Disclaimers</h2>
            <p>
              The website and all content are provided &quot;as is&quot; without warranty of any kind, express
              or implied, including warranties of merchantability, fitness for a particular purpose, and
              non-infringement. Property information, market estimates, school data, and AI-generated
              content are for informational purposes only and should not be relied upon as legal,
              financial, or appraisal advice.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3">10. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, Bespoke Realty and its affiliates, officers, agents,
              and employees shall not be liable for any indirect, incidental, special, consequential, or
              punitive damages arising out of or related to your use of the website or our services. Our
              total liability for any claim arising under these Terms is limited to the amount you paid us
              (if any) in the 12 months preceding the claim.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3">11. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless Bespoke Realty from any claim, loss, or expense
              (including reasonable attorneys&apos; fees) arising from your breach of these Terms or your misuse
              of the website.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3">12. Governing Law</h2>
            <p>
              These Terms are governed by the laws of the State of Tennessee, without regard to its
              conflict-of-laws principles. Any dispute will be resolved exclusively in the state or federal
              courts located in Shelby County, Tennessee.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3">13. Changes to These Terms</h2>
            <p>
              We may update these Terms from time to time. The &quot;Last Updated&quot; date at the top
              reflects the most recent revision. Continued use of the site after changes constitutes your
              acceptance of the revised Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3">14. Contact</h2>
            <div className="bg-[#faf8f4] border-l-4 border-[#BCA480] p-4 rounded-r">
              <p><strong>Bespoke Realty</strong></p>
              <p>7927 Players Forest Drive, Memphis, TN 38119</p>
              <p>Phone: <a href="tel:9013904414" className="text-[#BCA480] hover:underline">901-390-4414</a></p>
              <p>Email: <a href="mailto:info@bespokerealty.net" className="text-[#BCA480] hover:underline">info@bespokerealty.net</a></p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default Terms;
